<template>
    <v-row v-if="classifiersLoaded && instanceData">
        <v-col>
            <EditForm
                :account-id="accountId"
                :related-type="relatedType"
                :related-id="projectId"
                :instance-data="instanceData"
                :save-button-title="projectId ? $t('Save') : $t('Continue')"
                :allow-non-dirty-save="isCloned"
                edit-form-test-prefix="project_edit"
                @field_changed="fieldChanged"
                @update-dirty="hasDirtyGroups = $event"
                @close="closeEdit"
            >
                <template v-slot:header>
                    <div class="headline">{{ instanceData.name ? `${isEventTypeCompany(accountId) ? $t('Edit event') : $t('Edit project')} - ${instanceData.name}` : (isEventTypeCompany(accountId) ? $t('Add event') : $t('Add project')) }}</div>
                </template>
                <template v-slot:buttons-left="{}">
                    <v-btn v-if="projectId && canDeleteClient" color="error" text class="mr-2" @click="deleteClick">
                        {{ $t("Delete") }}
                    </v-btn>
                    <v-btn v-if="projectId" color="error" text class="mr-2" @click="archiveClick">
                        {{ !isArchived ? $t("Archive") : $t("Restore") }}
                    </v-btn>
                    <v-btn v-if="projectId && !isArchived" color="error" text class="mr-2" @click="cancelClick">
                        {{ isCancelled ? $t("Activate") : (isEventTypeCompany(accountId) ? $t('Cancel event') : $t("Cancel project")) }}
                    </v-btn>
                    <secondary-button
                        color="primary"
                        @click="onOpenAppStore"
                    >
                        {{ isEventTypeCompany(accountId) ? $t('Manage event fields') : $t('Manage project fields') }}
                    </secondary-button>
                </template>
            </EditForm>
        </v-col>
    </v-row>
</template>

<script>
import EditForm from "../../components/CustomForm/EditForm";
import {computed, ref, watch} from "@vue/composition-api";
import { $t } from "@/i18n";
import ProjectModule from "../../store/modules/project";
import AppModule from "../../store/modules/app";
import DialogModule from "../../store/modules/dialog";
import AccountChip from "../../components/AccountChip";
import ClientModule from "../../store/modules/client";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import AccountModule from "@/store/modules/account";
import {formatSysDate} from "@/plugins/formatting";
import {APP_STORE_APP_TYPES} from "@/helpers/constants";
import {Router} from "@/composables/Router";
import BreadcrumbsModule from "@/store/modules/breadcrumbs"
import appStoreDialog from "@/store/modules/AppStoreDialog/appStoreDialog";

export default {
    name: "ProjectEditView",
    components: {
        EditForm,
        AccountChip
    },
    setup(props, ctx) {
        const { current_account } = AccountModule();
        const { fetchClassifiers, setPageTitle, baseUrl, rights_masks, isNavigationWarning, custom_fields } = AppModule();
        const { fetchProject, deleteProject, projectAction, updateCache, clearCache, project_status, isEventTypeCompany } = ProjectModule();
        const { openConfirmDialog, setConfirmOptions, setDialogOptions } = DialogModule();
        const { fetchClient } = ClientModule();
        const { setBreadcrumbs } = BreadcrumbsModule();
        const { appStoreDialogActions } = appStoreDialog()
        const router = Router();

        const hasDirtyGroups = ref(false);
        const classifiersLoaded = ref(false);
        const instanceData = ref(null);
        const accountId = ref(null);
        const projectTypeId = ref(null);
        const isCloned = ref(false);

        const loadBreadcrumbs = () => {
            if (instanceData.value && projectId.value) {
                setBreadcrumbs([
                    {
                        to: {
                            name: 'projects_list',
                        },
                        name: isEventTypeCompany(accountId.value) ? $t('Events') : $t('Projects')
                    },
                    {
                        to: {
                            name: 'project_view',
                            params: {
                                id: projectId.value,
                            }
                        },
                        name: instanceData.value.name
                    }
                ], ctx.root._route.name)
            } else {
                setBreadcrumbs([
                    {
                        to: {
                            name: 'projects_list',
                        },
                        name: isEventTypeCompany(accountId.value) ? $t('Events') : $t('Projects')
                    },
                    {
                        name: isEventTypeCompany(accountId.value) ? $t('Add event') : $t('Add project')
                    }
                ], ctx.root._route.name)
            }
        }

        const pageTitle = computed(function () {
            if (isEventTypeCompany(accountId.value)) {
                if (ctx.root._route.name === "project_new") {
                    return $t('New event')
                } else {
                    return $t('Edit event')
                }
            } else {
                if (ctx.root._route.name === "project_new") {
                    return $t('New project')
                } else {
                    return $t('Edit project')
                }
            }
        });
        const projectId = computed(() => {
            if (ctx.root._route.name === "project_new")
                return 0;
            else
                return Number(ctx.root._route.params.id);
        });
        const relatedType = computed(() => {
            if (projectTypeId.value == null) return null;
            return "project.{0}".format(projectTypeId.value);
        });
        const isCancelled = computed(() => {
            if (instanceData.value === null) return false;
            return (instanceData.value.status_bit & project_status.value.cancelled) === project_status.value.cancelled;
        });
        const isArchived = computed(() => {
            if (instanceData.value === null) return false;
            return (instanceData.value.status_bit & project_status.value.archived) === project_status.value.archived;
        });
        const canDeleteClient = computed(() => {
            if (instanceData.value === null) return false;
            return (instanceData.value.rights & rights_masks.value.delete_client) === rights_masks.value.delete_client;
        })

        const loadClassifiers = async () => {
            let data = {
                types: ["custom_fields", "roles"],
                account_id: accountId.value
            };
            try {
                await fetchClassifiers(data)
                classifiersLoaded.value = true;
                setPageTitle(pageTitle.value);
            } catch (e) {
                errorDialog.showError(e)
            }
        };
        const loadData = async () => {
            let cloneId = ctx.root._route.query.clone_id ? parseInt(ctx.root._route.query.clone_id) : 0;
            if (cloneId) {
                isCloned.value = true
            }

            if (projectId.value > 0 || cloneId > 0) {
                fetchProject(projectId.value > 0 ? projectId.value : cloneId, true)
                    .then(resp => {
                        accountId.value = resp.account_id;
                        projectTypeId.value = resp.project_type_id;
                        loadClassifiers();

                        let projectStatus = null;
                        const projectStatusCustomField = custom_fields.value && custom_fields.value[accountId.value] ? custom_fields.value[accountId.value].find(x => x.type === 'project_status') : null
                        if (projectStatusCustomField &&
                            projectStatusCustomField._data &&
                            projectStatusCustomField._data.choices &&
                            projectStatusCustomField._data.choices[0] &&
                            projectStatusCustomField._data.choices[0].code) {
                            projectStatus = projectStatusCustomField._data.choices[0].code
                        }
                        if (cloneId > 0) {
                            resp = clearIds(resp);
                            resp.name = $t("{0} (Copy)").format(resp.name);
                            resp.status = projectStatus;  // on duplicate set status "active"
                            resp.status_bit = project_status.active;
                        }

                        instanceData.value = resp;
                        loadBreadcrumbs()
                    })
                    .catch(ex => errorDialog.showError(ex));
            } else {
                accountId.value = parseInt(ctx.root._route.params.account_id);
                projectTypeId.value = parseInt(ctx.root._route.params.project_type_id);
                await loadClassifiers();

                let projectStatus = null;
                const projectStatusCustomField = custom_fields.value && custom_fields.value[accountId.value] ? custom_fields.value[accountId.value].find(x => x.type === 'project_status') : null
                if (projectStatusCustomField &&
                    projectStatusCustomField._data &&
                    projectStatusCustomField._data.choices &&
                    projectStatusCustomField._data.choices[0] &&
                    projectStatusCustomField._data.choices[0].code
                ) {
                    projectStatus = projectStatusCustomField._data.choices[0].code
                }

                let clientId = ctx.root._route.query.client_id ? parseInt(ctx.root._route.query.client_id) : null;

                instanceData.value = {
                    id: 0,
                    account_id: accountId.value,
                    project_type_id: projectTypeId.value,
                    status_bit: project_status.active,
                    client_members: [],
                    name: "",
                    color: "color-{0}".format(Math.floor(Math.random() * 48 + 1)),
                    status: projectStatus,
                    manager_id: current_account.value.id,
                    start_date: formatSysDate(new Date()),
                    client_id: clientId
                };
                loadBreadcrumbs()
            }
        };
        const clearIds = obj => {
            obj.id = 0;
            let counter = 1;
            for (let prop in obj) {
                if (Object.prototype.hasOwnProperty.call(obj, prop)) {
                    if (Array.isArray(obj[prop])) {
                        for (let item of obj[prop]) {
                            if (item.id !== undefined) {
                                item.id = 0;
                                item.tmp_id = counter;
                                counter++;
                            }
                        }
                    }
                }
            }
            return obj;
        }
        const closeEdit = newObj => {
            if (newObj) {
                isNavigationWarning.value = false
            }

            if (newObj == null || projectId.value) {
                if (projectId.value === 0)
                    ctx.root._router.push({ name: "projects_list" });
                else
                    ctx.root._router.push({ name: "project_view", params: { id: projectId.value }});
            }
            else
                openWorkflowEdit(newObj.id, "none");
        };
        const openWorkflowEdit = (project_id, action) => {
            let newRoute = {
                name: "project_edit_workflow",
                params: { id: project_id, action }
            };
            let cloneId = ctx.root._route.query.clone_id ? parseInt(ctx.root._route.query.clone_id) : 0;
            if (cloneId > 0) {
                newRoute.query = { clone_id: cloneId };
            }
            ctx.root._router.push(newRoute);
        }
        const deleteClick = () => {
            setConfirmOptions({
                title: isEventTypeCompany(accountId.value) ? $t("Are you sure you wish to delete this event?") : $t("Are you sure you wish to delete this project?"),
                confirm_text: $t("Delete"),
                cancel_text: $t("Cancel"),
                action: deleteConfirmed
            });
            openConfirmDialog();
        };
        const deleteConfirmed = () => {
            deleteProject(projectId.value)
                .then(() => {
                    clearCache();
                    ctx.root._router.push({ name: "projects_list" });
                })
                .catch(ex => errorDialog.showError(ex));
        };
        const archiveClick = () => {
            if (isArchived.value) {
                archiveConfirmed();
                return;
            }
            setConfirmOptions({
                title: isEventTypeCompany(accountId.value) ? $t("Are you sure to archive this event?") : $t("Are you sure to archive this project?"),
                confirm_text: $t("Archive"),
                cancel_text: $t("Cancel"),
                action: archiveConfirmed
            });
            openConfirmDialog();
        };
        const archiveConfirmed = () => {
            executeAction(projectId.value, isArchived.value ? "unarchive" : "archive", resp => {
                updateCache(resp);
                closeEdit(null);
            });
        };
        const cancelClick = () => {
            if (isCancelled.value) {
                cancelConfirmed();
                return;
            }
            setConfirmOptions({
                title: isEventTypeCompany(accountId.value) ? $t("Are you sure to cancel this event?") : $t("Are you sure to cancel this project?"),
                confirm_text: isEventTypeCompany(accountId.value) ? $t("Cancel event") : $t("Cancel project"),
                cancel_text: $t("Close"),
                action: cancelConfirmed
            });
            openConfirmDialog();
        };
        const cancelConfirmed = () => {
            executeAction(projectId.value, isCancelled.value ? "activate" : "cancel", resp => {
                let wfAction = isCancelled.value ? "none" : "cancel";
                updateCache(resp);
                openWorkflowEdit(resp.id, wfAction);
            });
        };
        const executeAction = (project_id, action, callback) => {
            projectAction(projectId.value, action)
                .then(resp => callback(resp))
                .catch(ex => errorDialog.showError(ex));
        }
        const fieldChanged = ev => {
            if (ev.name === "client_id") {
                fetchClient({ client_id: ev.value, for_edit: 1 })
                    .then((resp) => {
                        instanceData.value.client_members = resp.members;
                    })
                    .catch(ex => errorDialog.showError(ex));
            }
        }

        const openAppStore = () => {
            router.push({ name: 'app_store', params: { accountId: accountId.value }})

            appStoreDialogActions.openAppStoreModal(
                APP_STORE_APP_TYPES.PROJECT,
                accountId.value,
                {
                    onUpdate: () => {},
                    dialogOptions: { defaultTab: 'fields' },
                }
            )
        }

        const onOpenAppStore = () => {
            if (hasDirtyGroups.value) {
                setDialogOptions({
                    component: () => import("@/views/Dialog/NavigationWarningDialog"),
                    payload: {
                        type: "clients_plan",
                        title: $t("Leave page?"),
                        description: $t(
                            "Changes that you made may not be saved"
                        ),
                        cancelButton: $t("Cancel"),
                        confirmButton: $t("Leave"),
                        nextVal: () => {
                            openAppStore();
                        }
                    }
                });
            } else {
                openAppStore()
            }
        };

        loadData();

        watch (() => hasDirtyGroups.value, () => {
            isNavigationWarning.value = Boolean(hasDirtyGroups.value)
        }, { immediate: true })

        return {
            baseUrl,
            classifiersLoaded,
            instanceData,
            pageTitle,
            accountId,
            relatedType,
            projectId,
            isCancelled,
            isArchived,
            canDeleteClient,
            fieldChanged,
            closeEdit,
            deleteClick,
            archiveClick,
            cancelClick,
            isCloned,
            hasDirtyGroups,
            onOpenAppStore,
            isEventTypeCompany
        };
    }
}
</script>
